import { useState, useEffect, useMemo } from "react";
import moment from "moment";
import FleetWebApi from "../Api/FleetWebApi";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Reports from "./Reports";

const OVERDUE_DAYS = 14;
const DEVICE_TYPES = ["Shuttie2", "Shuttie"];

function formatDate(value) {
  return value ? moment(value).format("YYYY-MM-DD HH:mm") : null;
}

function ShuttieStatus() {
  const [devices, setDevices] = useState([]);
  const [reportTimestamp, setReportTimestamp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceType, setDeviceType] = useState(DEVICE_TYPES[0]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    FleetWebApi.getShuttieCleaningStatus()
      .then((response) => {
        if (isMounted) {
          setDevices(response.devices || []);
          setReportTimestamp(response.timestamp);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setError(error.message);
          setIsLoading(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Memoized so the table only re-derives (and resets sort/paging) when a filter changes
  const rows = useMemo(() => {
    return devices
      .filter((device) => device.deviceType === deviceType)
      .map((device) => ({
        coreID: device.coreID,
        SerialNumber: device.serialNumber,
        LatestStatus: device.latestStatus || "Unknown",
        StatusReportedAt: formatDate(device.latestStatusTimestamp),
        LastCleaning: formatDate(device.lastCleaning) || "Never",
        DaysSinceCleaning:
          device.daysSinceCleaning == null ? "Never" : device.daysSinceCleaning,
        LastRequest: formatDate(device.lastRequest),
        DelayedUntil: formatDate(device.delayedUntil),
        LastHeard: formatDate(device.lastHeard),
      }));
  }, [devices, deviceType]);

  const showTable = !error && !isLoading && rows.length > 0;

  const controls = (
    <>
      <p>Online devices that have not had a cleaning cycle in the last {OVERDUE_DAYS} days</p>
      <ToggleButtonGroup
        value={deviceType}
        exclusive
        size="small"
        color="secondary"
        // Ignore clicks on the already-selected button so one type is always shown
        onChange={(event, newType) => newType && setDeviceType(newType)}
        sx={{ mr: 3 }}
      >
        {DEVICE_TYPES.map((type) => (
          <ToggleButton key={type} value={type}>
            {type}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </>
  );

  return (
    <>
      {!showTable && controls}
      {error && <p>Error Loading Data: {error}</p>}
      {!error && isLoading && (
        <>
          <p>Please wait while the Shuttie cleaning report loads...</p>
          <CircularProgress color="secondary" />
        </>
      )}
      {!error && !isLoading && rows.length === 0 && (
        <p>No overdue {deviceType} devices found.</p>
      )}
      {showTable && (
        <Reports
          data={rows}
          title={`${deviceType} Cleaning Overdue`}
          timestamp={reportTimestamp}
          alignTimestampRight
          enableColumnFilters
          showStatusField={false}
          headerContent={controls}
        />
      )}
    </>
  );
}

export default ShuttieStatus;
